import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

// Accept Interview Confirmation Modal Component
function AcceptInterviewModal({ isOpen, onClose, onConfirm, roleTitle, companyName, candidateStream, interviewAccepted, hasBookedFirstCall = false, onBookCall }) {
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  // Auto-close modal if interview is accepted and user has already booked
  useEffect(() => {
    if (interviewAccepted && hasBookedFirstCall) {
      // Close immediately if already booked
      onClose();
    }
  }, [interviewAccepted, hasBookedFirstCall, onClose]);

  const handleBookCall = () => {
    // Immediately open Calendly link and close modal
    window.open(getCalendlyLink(), '_blank');
    onClose(); // Close the modal immediately
    
    // Update the booking status in the background
    const updateBookingStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("Authentication token missing");
          return;
        }

        // Update the candidate's booking status
        const response = await axios.patch(
          `/api/candidates/updateCandidate`,
          {
            fields: {
              "booked first call (platform test)": true
            }
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data) {
          console.log("Successfully updated booking status");
          // Call the parent callback to update local state
          if (onBookCall) {
            onBookCall();
          }
        }
      } catch (error) {
        console.error("Error updating booking status:", error);
      }
    };

    // Run the API call in the background
    updateBookingStatus();
  };

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await onConfirm();
    } finally {
      setIsAccepting(false);
    }
  };

  const getCalendlyLink = () => {
    const stream = candidateStream && typeof candidateStream === 'string' ? candidateStream.toLowerCase() : '';
    console.log('AcceptInterviewModal - candidateStream:', candidateStream);
    console.log('AcceptInterviewModal - stream (lowercase):', stream);
    if (stream.includes('sales')) {
      console.log('AcceptInterviewModal - Using SALES Calendly link');
      return 'https://calendly.com/d/cv4g-cnm-zrd/jumpstart-sales-first-interview-prep-call';
    }
    console.log('AcceptInterviewModal - Using GENERALIST/MARKETING Calendly link');
    // Generalist and Marketing share the same link
    return 'https://calendly.com/d/cv8m-cj2-9qz/jumpstart-generalists-first-interview-prep-call?month=2025-07';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        className="bg-white w-full max-w-lg p-6 rounded-xl shadow-xl max-h-[90vh] overflow-y-auto relative"
      >
        {interviewAccepted ? (
          <div>
            {/* X button in top right */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">🎉</span>
              <h2 className="text-xl font-semibold text-gray-800">Congratulations!</h2>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              You've accepted the interview request for: <strong className="text-gray-700">{roleTitle} at {companyName}</strong>. Good luck!
            </p>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                <span className="mr-2">📞</span>
                Book Your Mandatory Prep Call
              </h3>
              <p className="text-xs text-gray-700 mb-3">
                <strong>Required first step:</strong> Book a prep session with our expert coaches to set you up for success.
              </p>
              
              <div className="bg-gray-50 rounded-md p-3 mb-4">
                <p className="text-xs font-medium text-gray-800 mb-2">What we'll cover:</p>
                <div className="space-y-1 text-xs text-gray-700">
                  <div className="flex items-center">
                    <span className="w-1 h-1 bg-[#02B491] rounded-full mr-2"></span>
                    <span>Live elevator pitch run-through with feedback</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-1 h-1 bg-[#02B491] rounded-full mr-2"></span>
                    <span>Essential prep tips & how to present yourself</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-1 h-1 bg-[#02B491] rounded-full mr-2"></span>
                    <span>Q&A time - ask anything about the process</span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleBookCall}
                className="inline-block w-full text-center px-4 py-2 text-sm font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-lg transition-colors"
              >
                Book Prep Session
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Accept Interview Request</h2>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              You're about to accept the interview request for: <strong className="text-gray-700">{roleTitle} at {companyName}</strong>.
            </p>
            
            {/* Interview Rules Checklist */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
              <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📋</span>
                Interview Commitment
              </h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="font-medium">Email the startup within 24 hrs with your availability</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="font-medium">Test your tech (e.g. Zoom/Google Meet) in advance</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="font-medium">Join the interview at least 5 mins early</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="font-medium">Send a thank you email straight after the interview</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="font-medium">Complete any case study task before the deadline</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="font-medium">Only cancel if absolutely necessary, and with 24+ hrs notice</span>
                </div>
              </div>
            </div>

            {/* Agreement Checkbox */}
            <div className="flex items-start space-x-3 mb-6">
              <input
                type="checkbox"
                id="interview-accept-agreement"
                checked={agreementChecked}
                onChange={(e) => setAgreementChecked(e.target.checked)}
                className="mt-1 h-4 w-4 text-[#02B491] focus:ring-[#02B491] border-gray-300 rounded"
              />
              <label htmlFor="interview-accept-agreement" className="text-sm text-gray-700 cursor-pointer">
                I understand that I'll get a strike if I miss interviews, cancel late, miss case study deadlines or don't follow up after an intro. I understand that 2 strikes will lead to my offboarding from Jumpstart.
              </label>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={!agreementChecked || isAccepting}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  agreementChecked && !isAccepting
                    ? 'bg-[#02B491] hover:bg-[#018268] cursor-pointer' 
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {isAccepting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Accepting...
                  </>
                ) : (
                  'Accept Interview'
                )}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default AcceptInterviewModal;
